// src/pages/plan-summary/page.tsx

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import WeightJourneyChart from './components/WeightJourneyChart';
import WeeklyBreakdown from './components/WeeklyBreakdown';
import AchievementsBadges from './components/AchievementsBadges';
import BestWorstDays from './components/BestWorstDays';
import InsightsCard from './components/InsightsCard';
import NextStepsCard from './components/NextStepsCard';
import SummaryStats from './components/SummaryStats';
import UpgradeModal from '../../components/vip/UpgradeModal';
import { usePlanSummary } from '../../hooks/usePlanSummary';
import { useAuthStore } from '../../stores/authStore';

export default function PlanSummaryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { user } = useAuthStore();

  const {
    summary,
    plan,
    isLoading,
    isGenerating,
    error,
    loadSummary,
    downloadPDF,
    planEndDate,
    formatDate,
  } = usePlanSummary();

  const isVip = user?.plan === 'VIP';

  const handleDownloadPDF = () => {
    if (isVip) {
      downloadPDF();
    } else {
      setShowUpgradeModal(true);
    }
  };

  // Loading state
  if (isLoading || isGenerating) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] ">
        <div className="text-center">
          <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6 relative">
            <div className="absolute inset-0 border-t-2 border-yellow-400 rounded-full animate-spin"></div>
            <i className="ri-sparkle-line text-yellow-400 text-3xl"></i>
          </div>
          <h2 className="text-2xl font-light text-white mb-2 tracking-wide">
            {isGenerating ? t('planSummary.loading.generating') : t('planSummary.loading.title')}
          </h2>
          <p className="text-gray-500 text-sm">
            {isGenerating ? t('planSummary.loading.generatingDescription') : t('planSummary.loading.description')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md p-8 bg-gray-900/50 rounded-2xl border border-gray-800 backdrop-blur-sm">
          <i className="ri-error-warning-line text-red-400 text-5xl mb-4 inline-block"></i>
          <h2 className="text-xl font-medium text-white mb-2">{t('planSummary.error.title')}</h2>
          <p className="text-gray-400 text-sm mb-8 leading-relaxed">{error}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={loadSummary} className="px-6 py-2.5 bg-white/5 hover:bg-white/10 text-white text-sm font-medium rounded-lg transition cursor-pointer border border-white/10">
              {t('planSummary.error.tryAgain')}
            </button>
            <button onClick={() => navigate('/dashboard')} className="px-6 py-2.5 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 text-sm font-medium rounded-lg transition cursor-pointer border border-yellow-500/20">
              {t('planSummary.error.backToDashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!summary || !plan || !planEndDate) return null;

  const hasAchievements = summary.achievements_unlocked && summary.achievements_unlocked.length > 0;

  return (
    <div>
      {/* Increased vertical spacing (space-y-12 md:space-y-16) to prevent overlap */}
      <div className="max-w-6xl mx-auto space-y-12 md:space-y-16 py-16 px-4">

        {/* 1. THE HERO */}
        <section className="relative rounded-3xl p-8 lg:p-10 border border-white/5 overflow-hidden bg-[#0a0a0a]">
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500/10 rounded-full blur-[100px] pointer-events-none translate-x-1/3 -translate-y-1/3"></div>

          <div className="relative z-10 flex flex-col md:flex-row gap-8 justify-between items-start">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-yellow-400 text-xs font-semibold tracking-widest uppercase mb-4">
                <i className="ri-trophy-line"></i> {t('planSummary.header.title')}
              </div>
              <h1 className="text-4xl md:text-5xl font-light text-white mb-3 tracking-tight">
                {plan.title || `${plan.duration_days}-${t('nutritionPlan.list.day')} ${t('common.nutritionPlan')}`}
              </h1>
              <p className="text-gray-400 flex items-center gap-2 text-sm">
                <i className="ri-calendar-event-line"></i>
                {formatDate(new Date(plan.plan_start_date))} — {formatDate(planEndDate)}
              </p>
            </div>

            <button
              onClick={handleDownloadPDF}
              className="group flex items-center gap-3 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all cursor-pointer backdrop-blur-sm shrink-0"
            >
              <div className="w-8 h-8 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-download-cloud-2-line"></i>
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white flex items-center gap-1.5">
                  {t('planSummary.header.actions.downloadPdf')}
                  {!isVip && <i className="ri-vip-crown-line text-yellow-400 text-[10px]"></i>}
                </div>
                <div className="text-xs text-gray-500">{t('planSummary.header.actions.saveJourney')}</div>
              </div>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 pt-8 border-t border-white/5">
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{t('planSummary.quickStats.weightChange')}</div>
              <div className="text-3xl font-light text-white">
                <span className={summary.weight_change < 0 ? 'text-green-400' : 'text-white'}>
                  {summary.weight_change > 0 ? '+' : ''}{summary.weight_change.toFixed(1)}
                </span>
                <span className="text-lg text-gray-500 ml-1">kg</span>
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{t('planSummary.quickStats.completion')}</div>
              <div className="text-3xl font-light text-white">
                {Math.round(summary.completion_percentage)}<span className="text-lg text-gray-500 ml-1">%</span>
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{t('planSummary.quickStats.adherence')}</div>
              <div className="text-3xl font-light text-white">
                {Math.round(summary.meal_adherence_percentage)}<span className="text-lg text-gray-500 ml-1">%</span>
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{t('planSummary.quickStats.goalStatus')}</div>
              <div className="text-xl font-medium mt-1 flex items-center gap-2">
                {summary.goal_achieved ? (
                  <span className="text-yellow-400 flex items-center gap-2"><i className="ri-medal-fill"></i> {t('planSummary.quickStats.achieved')}</span>
                ) : (
                  <span className="text-blue-400 flex items-center gap-2"><i className="ri-route-line"></i> {t('planSummary.quickStats.inProgress')}</span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 2. THE JOURNEY */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 px-2">
            <h2 className="text-xl font-light text-white">{t('planSummary.sections.journey')}</h2>
            <div className="h-px bg-white/10 flex-1"></div>
          </div>
          <SummaryStats summary={summary} />
          <WeightJourneyChart
            startWeight={summary.start_weight}
            endWeight={summary.end_weight}
            goalWeight={summary.goal_weight}
            planStartDate={plan.plan_start_date}
            durationDays={plan.duration_days}
          />
        </section>

        {/* 3. THE MICRO VIEW */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="flex items-center gap-3 px-2">
              <h2 className="text-xl font-light text-white">{t('planSummary.sections.weeklyPerformance')}</h2>
              <div className="h-px bg-white/10 flex-1"></div>
            </div>
            <WeeklyBreakdown weeklyStats={summary.weekly_stats} />
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-3 px-2">
              <h2 className="text-xl font-light text-white">{t('planSummary.sections.dailyHighlights')}</h2>
              <div className="h-px bg-white/10 flex-1"></div>
            </div>
            <BestWorstDays bestDays={summary.best_days} worstDays={summary.worst_days} />
          </div>
        </section>

        {/* 4. THE BRAIN */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Dynamically expand if no achievements */}
          <div className={`${hasAchievements ? 'lg:col-span-5' : 'lg:col-span-12'} space-y-6`}>
            <div className="flex items-center gap-3 px-2">
              <h2 className="text-xl font-light text-white">{t('planSummary.sections.aiAnalysis')}</h2>
              <div className="h-px bg-white/10 flex-1"></div>
            </div>
            <InsightsCard insights={summary.insights} />
          </div>

          {hasAchievements && (
            <div className="lg:col-span-7 space-y-6">
              <div className="flex items-center gap-3 px-2">
                <h2 className="text-xl font-light text-white">{t('planSummary.sections.trophyRoom')}</h2>
                <div className="h-px bg-white/10 flex-1"></div>
              </div>
              <AchievementsBadges achievements={summary.achievements_unlocked} />
            </div>
          )}
        </section>

        {/* 5. THE NEXT CHAPTER */}
        <section className="pt-4">
          <NextStepsCard
            goalAchieved={summary.goal_achieved}
            adherence={summary.meal_adherence_percentage}
            planGoal={plan.goal}
          />
        </section>

      </div>

      {showUpgradeModal && (
        <UpgradeModal feature="pdf_export" onClose={() => setShowUpgradeModal(false)} />
      )}
    </div>
  );
}